import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Box, Checkbox, Slide, Stack, TextField, Typography } from '@mui/material';
import { theme } from '../../../constants/theme';
import { IProject, IThspianPlaylist } from '../../../types/global/helper';
import { useThemeContext } from '../../../hook/useThemeManager';
import { PlaylistCard } from './PlaylistCard';
import { isEmpty } from '../../../utils/lodash';
import CustomButton from '../../Button/CustomButton';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { TransitionProps } from '@chakra-ui/react';
import { updatePlaylist } from '../../../redux/project/actions';
import { useDispatch } from 'react-redux';
import CloseIcon from '../../CustomSVGIcons/CloseIcon';

export interface IAvatarSelection {
    userPlaylists: IThspianPlaylist[];
    open: boolean;
    onClose: () => void;
    selectedProject?: IProject;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const AddToPlaylistModal: React.FC<IAvatarSelection> = ({ userPlaylists, open, onClose, selectedProject }) => {
    const [selectedPlaylist, setSelectedPlaylist] = useState<IThspianPlaylist[]>([]);
    const [playlists, setPlaylists] = useState<IThspianPlaylist[]>(userPlaylists);
    const [, setSearchValue] = useState('');
    const { themeStyle, device } = useThemeContext();
    const dispatch = useDispatch();

    const handleCheckboxChange = (playlist: IThspianPlaylist) => {
        const isChecked = selectedPlaylist?.some(a => a._id === playlist._id);
        if (isChecked) {
            const filtered = selectedPlaylist.filter(p => p._id !== playlist._id);
            setSelectedPlaylist(filtered);
        } else {
            setSelectedPlaylist(prevState => [...prevState, playlist]);
        }
    };

    const handleSearchInputChange = (value: string) => {
        setSearchValue(value);
        if (value.length > 3) {
            const filtered = userPlaylists?.filter(playlist => playlist.name.startsWith(value));
            if (filtered) {
                setPlaylists(filtered);
            }
        }

        if (value.length < 1 && userPlaylists) {
            setPlaylists(userPlaylists);
        }
    };

    const handleConfirmPlaylists = () => {
        if (selectedProject) {
            selectedPlaylist.forEach(playlist => {
                dispatch(
                    updatePlaylist({
                        id: playlist._id,
                        project: selectedProject._id,
                    }),
                );
            });
            setSelectedPlaylist([]);
            onClose && onClose();
        }
    };

    useEffect(() => {
        setPlaylists(userPlaylists);
    }, [userPlaylists]);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                color="blue"
                TransitionComponent={Transition}
                fullWidth={false}
                fullScreen={false}
                maxWidth={'lg'}
                PaperProps={{
                    style: {
                        backgroundColor: theme[themeStyle].background_gradient.start,
                        height: device.isDesktop ? '50%' : '100%',
                        width: device.isDesktop ? '30%' : '100%',
                        overflowY: 'scroll',
                        border: device.isDesktop ? '1px solid grey' : 'none',
                        borderRadius: device.isDesktop ? 20 : 0,
                        backdropFilter: 'blur(20px)',
                        maxHeight: '100vh',
                        maxWidth: '100vw',
                        margin: 0,
                    },
                }}>
                <TextField
                    autoFocus
                    variant="outlined"
                    placeholder={`Search and select playlist ...`}
                    color="secondary"
                    InputProps={{
                        placeholder: `Search and select playlist ...`,
                        style: {
                            color: `${theme[themeStyle].tag_white}`, // Change the placeholder color to white
                            border: `1px solid ${theme[themeStyle].tag_white}`, // Set the border width and color
                            borderRadius: '50px', // Set the border radius
                            paddingLeft: '8px', // Add some padding for the text inside the input field
                            fontSize: 12,
                            minWidth: 200,
                        },
                    }}
                    inputProps={{
                        color: 'blue',
                    }}
                    // Add your search functionality here
                    onChange={e => {
                        handleSearchInputChange(e.target.value);
                    }}
                    sx={{
                        px: 3,
                        mt: 3,
                    }}
                />
                <DialogContent>
                    <Box sx={{ mb: 5 }}>
                        {playlists && playlists.length > 0 ? (
                            playlists.map((playlist, index) => (
                                <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    key={index}
                                    mb={1}>
                                    <Stack flexDirection="row" alignItems="center" gap={0.5}>
                                        <PlaylistCard playlist={playlist} size={50} />
                                        <Box ml={1}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 14,
                                                    color: theme[themeStyle].tag_white,
                                                }}>
                                                {playlist.name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                    fontSize: 8,
                                                    color: theme[themeStyle].tag_white,
                                                }}>
                                                {playlist.description}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Checkbox
                                        sx={{
                                            color: theme[themeStyle].lightWhite,
                                        }}
                                        color="secondary"
                                        checked={selectedPlaylist.some(a => a._id === playlist._id)}
                                        onChange={() => handleCheckboxChange(playlist)}
                                    />
                                </Stack>
                            ))
                        ) : (
                            <Typography color="white" sx={{ textAlign: 'center', mt: 2 }}>
                                No Playlists Found
                            </Typography>
                        )}
                    </Box>

                    <CustomButton
                        onClick={handleConfirmPlaylists}
                        label="Update Playlist"
                        disabled={isEmpty(selectedPlaylist)}
                        sx={{
                            position: 'fixed',
                            bottom: 15,
                            right: 15,
                            left: 15,
                            zIndex: 9999,
                        }}
                    />
                </DialogContent>

                <DialogActions sx={{ position: 'absolute', right: 0, top: 0, p: 0 }}>
                    <CloseIcon onClick={onClose} stroke={theme[themeStyle].tag_white} />
                </DialogActions>
            </Dialog>
        </>
    );
};

export { AddToPlaylistModal };
